import React, { MouseEvent, useCallback, useMemo, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  IconButton,
  Paper,
  Popover,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { useFormatDateHook } from "hooks";
import { useDealSubAssetsSelector } from "store";
import { AmortizationColumn, AmortizationData, AmortizationRow } from "store/types";
import { formatNumber } from "utils";

import { AmortizationTableRow } from "./AmortizationTableRow";

type AmortizationTableProps = {
  table: AmortizationData["table"];
  rows?: AmortizationRow[];
  onMenuOpen?: (event: MouseEvent<HTMLElement>, index: number) => void;
  onChildMenuOpen?: (event: MouseEvent<HTMLElement>, index: number, childIndex: number) => void;
  columnVisibility?: Record<string, boolean>;
  sx?: SxProps;
};

type RowFragmentProps = {
  row: AmortizationRow;
  index: number;
  columns: AmortizationColumn[];
  showExpandCell: boolean;
  isExpanded: boolean;
  onExpandClick: (row: AmortizationRow) => void;
  onMenuOpen?: (event: MouseEvent<HTMLElement>, index: number) => void;
  onChildMenuOpen?: (event: MouseEvent<HTMLElement>, index: number, childIndex: number) => void;
  onSubDealClick: (e: MouseEvent<HTMLElement>, children: AmortizationRow[]) => void;
  formatCellValue: (value: unknown, column: AmortizationColumn) => string | number;
};

type ChildRowProps = {
  childRow: AmortizationRow;
  childIndex: number;
  parentIndex: number;
  columns: AmortizationColumn[];
  showExpandCell: boolean;
  isLastChild: boolean;
  onChildMenuOpen?: (event: MouseEvent<HTMLElement>, index: number, childIndex: number) => void;
  onSubDealClick: (e: MouseEvent<HTMLElement>, children: AmortizationRow[]) => void;
  formatCellValue: (value: unknown, column: AmortizationColumn) => string | number;
};

const ChildRow: React.FC<ChildRowProps> = React.memo(
  ({
    childRow,
    childIndex,
    parentIndex,
    columns,
    showExpandCell,
    isLastChild,
    onChildMenuOpen,
    onSubDealClick,
    formatCellValue
  }) => {
    const handleChildMenuOpen = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        onChildMenuOpen?.(e, parentIndex, childIndex);
      },
      [onChildMenuOpen, parentIndex, childIndex]
    );

    return (
      <AmortizationTableRow
        row={childRow}
        columns={columns}
        isChild={true}
        showExpandCell={showExpandCell}
        showBorderBottom={isLastChild}
        onMenuOpen={handleChildMenuOpen}
        onSubDealClick={onSubDealClick}
        formatCellValue={formatCellValue}
      />
    );
  }
);

const RowFragment: React.FC<RowFragmentProps> = React.memo(
  ({
    row,
    index,
    columns,
    showExpandCell,
    isExpanded,
    onExpandClick,
    onMenuOpen,
    onChildMenuOpen,
    onSubDealClick,
    formatCellValue
  }) => {
    const handleMenuOpen = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        onMenuOpen?.(e, index);
      },
      [onMenuOpen, index]
    );

    const expandButton = useMemo(() => {
      if (!row.children) return undefined;
      return (
        <IconButton onClick={() => onExpandClick(row)}>
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      );
    }, [isExpanded, row, onExpandClick]);

    return (
      <>
        <AmortizationTableRow
          row={row}
          columns={columns}
          showExpandCell={showExpandCell}
          expandButton={expandButton}
          onMenuOpen={handleMenuOpen}
          onSubDealClick={onSubDealClick}
          formatCellValue={formatCellValue}
          showBorderBottom={!isExpanded}
        />
        {isExpanded &&
          row.children?.map((childRow, childIndex) => (
            <ChildRow
              key={`${row.date}-child-${childIndex}`}
              childRow={childRow}
              childIndex={childIndex}
              parentIndex={index}
              columns={columns}
              showExpandCell={showExpandCell}
              isLastChild={childIndex === row.children!.length - 1}
              onChildMenuOpen={onChildMenuOpen}
              onSubDealClick={onSubDealClick}
              formatCellValue={formatCellValue}
            />
          ))}
      </>
    );
  }
);

export const AmortizationTable: React.FC<AmortizationTableProps> = ({
  table,
  rows: rowsProp,
  onMenuOpen,
  onChildMenuOpen,
  columnVisibility: controlledColumnVisibility,
  sx
}) => {
  const subAssets = useDealSubAssetsSelector();
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverChildren, setPopoverChildren] = useState<AmortizationRow[]>([]);
  const formatDate = useFormatDateHook();

  const columnVisibility = controlledColumnVisibility ?? {};

  const columns = useMemo(() => {
    if (Object.keys(columnVisibility).length > 0) {
      return table.columns.filter((col) => columnVisibility[col.key] !== false);
    }
    return table.columns.filter((col) => col.visible);
  }, [table.columns, columnVisibility]);

  const rows = useMemo(() => rowsProp ?? table.rows.filter((row) => !row.is_report), [rowsProp, table.rows]);

  const getRowId = (row: AmortizationRow) => `${row.segmentIds.join(",")}-${row.date}`;

  const handleExpandClick = useCallback((row: AmortizationRow) => {
    const rowId = getRowId(row);
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const handleSubDealClick = useCallback((e: MouseEvent<HTMLElement>, children: AmortizationRow[]) => {
    setPopoverAnchor(e.currentTarget);
    setPopoverChildren(children);
  }, []);

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverChildren([]);
  };

  const isPopoverOpen = Boolean(popoverAnchor);

  const formatCellValue = useCallback(
    (value: unknown, column: AmortizationColumn): string | number => {
      if (column.type === "date") {
        return formatDate({ date: value as string });
      }
      if (column.suffix && typeof value === "number") {
        const formattedNumber = formatNumber(value);
        return column.suffix === "%" ? `${formattedNumber}${column.suffix}` : `${column.suffix}${formattedNumber}`;
      }
      return value as string | number;
    },
    [formatDate]
  );

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        overflow: "auto",
        border: "1px solid #DBE8F5",
        borderRadius: "8px",
        "& .MuiTable-root": {
          borderCollapse: "separate",
          borderSpacing: 0
        },
        ...sx
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {subAssets.length > 0 && (
              <TableCell
                sx={{ width: "40px", minWidth: "40px", padding: "6px 6px", position: "sticky", left: 0, zIndex: 4, bgcolor: "background.paper" }}
              />
            )}
            <TableCell
              sx={{
                width: "32px",
                minWidth: "32px",
                padding: "6px",
                textAlign: "center",
                position: "sticky",
                left: subAssets.length > 0 ? 40 : 0,
                zIndex: 4,
                bgcolor: "background.paper"
              }}
            />
            {columns.map((column, colIndex) => (
              <TableCell
                key={column.key}
                sx={
                  colIndex === 0
                    ? {
                        position: "sticky",
                        left: subAssets.length > 0 ? 72 : 32,
                        zIndex: 4,
                        bgcolor: "background.paper",
                      }
                    : undefined
                }
              >
                {column.label}
              </TableCell>
            ))}
            <TableCell width={50} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const rowId = getRowId(row);
            const isExpanded = expandedRows[rowId];

            return (
              <RowFragment
                key={row.date}
                row={row}
                index={index}
                columns={columns}
                showExpandCell={subAssets.length > 0}
                isExpanded={isExpanded}
                onExpandClick={handleExpandClick}
                onMenuOpen={onMenuOpen}
                onChildMenuOpen={onChildMenuOpen}
                onSubDealClick={handleSubDealClick}
                formatCellValue={formatCellValue}
              />
            );
          })}
        </TableBody>
      </Table>
      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        sx={{
          "& .MuiPopover-paper": {
            padding: "8px 12px",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
          }
        }}
      >
        <Stack spacing={1}>
          {popoverChildren.map((child, index) => (
            <NavigateLink
              key={index}
              allowNewTab={true}
              route="dealDashboard"
              params={{ deal_id: String(child.deal_id) }}
              style={{ color: "#26a6ae", fontSize: "14px" }}
            >
              {child.sub_deal}
            </NavigateLink>
          ))}
        </Stack>
      </Popover>
    </TableContainer>
  );
};
